import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { colors } from '../theme/tokens'
import { useGetVehiclesQuery } from '../services/vehicle'
import { useLazyGetHistoriesForDayQuery } from '../services/histories'
import { pickIoFirst } from '../helpers/telemetry'
import type { History, Vehicle } from '../types'

type BarPoint = { value: number; label: string; frontColor: string }

function toRadians(value: number): number {
  return (value * Math.PI) / 180
}

function distanceInKm(a: History, b: History): number {
  const earthRadiusKm = 6371
  const dLat = toRadians(b.latitude - a.latitude)
  const dLon = toRadians(b.longitude - a.longitude)
  const lat1 = toRadians(a.latitude)
  const lat2 = toRadians(b.latitude)
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
  return earthRadiusKm * c
}

function ignitionOnFromHistory(row: History): boolean | undefined {
  if (row.event === 'ignitionOn') return true
  if (row.event === 'ignitionOff') return false
  const ignitionRaw = pickIoFirst(row.io as never, ['Ignition', 'Digital Input 1'])
  const ignitionNumeric = Number(ignitionRaw)
  if (Number.isFinite(ignitionNumeric)) {
    if (ignitionNumeric === 1 || ignitionNumeric === 32512) return true
    if (ignitionNumeric === 0) return false
  }
  return undefined
}

export function useReport() {
  const [params, setParams] = useSearchParams()
  const paramVehicleId = params.get('vehicleId') ?? undefined

  const { data, isLoading: vehiclesLoading } = useGetVehiclesQuery()
  const [getHistoriesForDay] = useLazyGetHistoriesForDayQuery()
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null)
  const [startDate, setStartDate] = useState(moment().subtract(1, 'week').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'))
  const [reportLoading, setReportLoading] = useState(false)
  const [barData, setBarData] = useState<BarPoint[]>([])
  const [totalDistanceKm, setTotalDistanceKm] = useState(0)
  const [totalEngineHours, setTotalEngineHours] = useState(0)
  const [drivingPct, setDrivingPct] = useState(0)

  const vehicles = useMemo(() => data?.data || [], [data?.data])

  useEffect(() => {
    if (vehicles.length === 0) {
      setCurrentVehicle(null)
      return
    }
    if (paramVehicleId) {
      const match = vehicles.find((v) => String(v._id) === String(paramVehicleId))
      if (match) {
        setCurrentVehicle(match)
        params.delete('vehicleId')
        setParams(params, { replace: true })
        return
      }
    }
    if (!currentVehicle) {
      setCurrentVehicle(vehicles[0])
      return
    }
    const still = vehicles.find((v) => String(v._id) === String(currentVehicle?._id))
    if (!still) setCurrentVehicle(vehicles[0])
  }, [vehicles, currentVehicle, paramVehicleId, params, setParams])

  useEffect(() => {
    const vehicleId = currentVehicle?._id
    if (!vehicleId || !startDate || !endDate) {
      setBarData([])
      setTotalDistanceKm(0)
      setTotalEngineHours(0)
      setDrivingPct(0)
      return
    }

    let cancelled = false
    const run = async () => {
      setReportLoading(true)
      try {
        const days: string[] = []
        let cursor = moment(startDate)
        let count = 0
        while (cursor.isSameOrBefore(endDate) && count < 62) {
          days.push(cursor.format('YYYY-MM-DD'))
          cursor = cursor.add(1, 'day')
          count += 1
        }

        const dayResponses = await Promise.all(
          days.map((day) => getHistoriesForDay({ vehicleId, day, limit: 2000, lean: 0 }).unwrap())
        )

        let cumulativeDistance = 0
        let drivingMinutes = 0
        let idleMinutes = 0

        const computedBars = dayResponses.map((response, index) => {
          const rows = (response?.rows ?? []).slice().sort((a, b) => a.timestamp - b.timestamp)
          let dayDistance = 0
          for (let i = 1; i < rows.length; i += 1) {
            const prev = rows[i - 1]
            const curr = rows[i]
            if (
              !Number.isFinite(prev.latitude) ||
              !Number.isFinite(prev.longitude) ||
              !Number.isFinite(curr.latitude) ||
              !Number.isFinite(curr.longitude)
            ) {
              continue
            }
            dayDistance += distanceInKm(prev, curr)
          }
          cumulativeDistance += dayDistance

          for (let i = 1; i < rows.length; i += 1) {
            const prev = rows[i - 1]
            const curr = rows[i]
            const deltaMs = curr.timestamp - prev.timestamp
            if (deltaMs <= 0) continue
            const cappedMs = Math.min(deltaMs, 30 * 60 * 1000)
            if (!ignitionOnFromHistory(prev)) continue
            const avgSpeed = ((prev.speed || 0) + (curr.speed || 0)) / 2
            if (avgSpeed > 5) drivingMinutes += cappedMs / (60 * 1000)
            else idleMinutes += cappedMs / (60 * 1000)
          }

          return {
            value: Number(dayDistance.toFixed(1)),
            label: moment(days[index]).format('DD/MM'),
            frontColor: colors.SECONDARY_BLUE,
          }
        })

        const totalEngineMinutes = drivingMinutes + idleMinutes
        const computedDrivingPct =
          totalEngineMinutes > 0 ? Math.round((drivingMinutes / totalEngineMinutes) * 100) : 0

        if (!cancelled) {
          setBarData(computedBars)
          setTotalDistanceKm(Number(cumulativeDistance.toFixed(1)))
          setTotalEngineHours(Number((totalEngineMinutes / 60).toFixed(1)))
          setDrivingPct(computedDrivingPct)
        }
      } catch {
        if (!cancelled) {
          setBarData([])
          setTotalDistanceKm(0)
          setTotalEngineHours(0)
          setDrivingPct(0)
        }
      } finally {
        if (!cancelled) setReportLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [currentVehicle?._id, endDate, getHistoriesForDay, startDate])

  const idlePct = useMemo(() => Math.max(0, 100 - drivingPct), [drivingPct])

  return {
    vehicles,
    isLoading: vehiclesLoading,
    reportLoading,
    currentVehicle,
    setCurrentVehicle,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    barData,
    totalDistanceLabel: `${Math.round(totalDistanceKm).toLocaleString('fr-FR')} km`,
    totalEngineLabel: `${totalEngineHours.toLocaleString('fr-FR', {
      minimumFractionDigits: totalEngineHours % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 1,
    })}h`,
    drivingPct,
    idlePct,
  }
}
